// ⚙️ Trading › Binance — configuración del bot (cuenta global). Usa las RPCs del esquema
// `trading`: config_get / config_set / set_api_keys / kill_switch_set. Claves API
// write-only (se guardan cifradas). El "activo" (iniciar/detener) y "probar conexión"
// se cablearán con el motor Python (Fase 1.2); aquí el control de seguridad es el kill switch.
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Settings, Loader2, Save, KeyRound, ShieldAlert, ShieldCheck, Activity, Play, Square } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { LOCALE } from '@/lib/utils/regional';

const TR = { headers: { 'Accept-Profile': 'trading', 'Content-Profile': 'trading' } };
const inputCls = 'h-9 w-full rounded-md border border-input bg-background px-2 text-sm';
const TIMEFRAMES = ['1m', '5m', '15m', '30m', '1h', '4h', '1d'];

interface Estado { balance_usd: number | null; equity_usd: number | null; heartbeat: string | null; posiciones_abiertas: number | null }
interface Cfg {
  estrategia: string; symbol: string; timeframe: string;
  params: { sma_rapida?: number; sma_lenta?: number; sma_tendencia?: number; umbral_cruce_pct?: number };
  riesgo: Record<string, number | boolean>;
  ambiente: string; activo: boolean; kill_switch: boolean; kill_switch_motivo: string | null;
  api_configurada: boolean; estado: Estado | null;
}

const RIESGO_CAMPOS: Array<{ k: string; label: string }> = [
  { k: 'riesgo_por_trade_pct', label: 'Riesgo por operación (fracción; 0.01 = 1%)' },
  { k: 'max_posicion_pct', label: 'Máx. por posición (fracción)' },
  { k: 'max_exposicion_pct', label: 'Máx. exposición total (fracción)' },
  { k: 'max_posiciones', label: 'Máx. posiciones simultáneas' },
  { k: 'perdida_diaria_max_pct', label: 'Pérdida diaria máx. (fracción) → kill switch' },
  { k: 'drawdown_max_pct', label: 'Drawdown máx. (fracción) → kill switch' },
  { k: 'max_perdidas_consecutivas', label: 'Máx. pérdidas consecutivas' },
  { k: 'max_ordenes_por_hora', label: 'Máx. órdenes por hora' },
  { k: 'atr_stop_mult', label: 'Stop-loss (× ATR)' },
];

export function TradingBinance() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data, isLoading } = useQuery<Cfg>({
    queryKey: ['trading-config'],
    queryFn: async () => (await apiClient.post('/rpc/config_get', {}, TR)).data,
    refetchInterval: 15000, // refresca heartbeat/estado
  });

  const [f, setF] = useState<Cfg | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [apiSecret, setApiSecret] = useState('');
  useEffect(() => { if (data) setF(data); }, [data]);

  const set = (k: keyof Cfg, v: unknown) => setF((p) => (p ? { ...p, [k]: v } : p));
  const setParam = (k: string, v: string) => setF((p) => (p ? { ...p, params: { ...p.params, [k]: Number(v) } } : p));
  const setRiesgo = (k: string, v: string) => setF((p) => (p ? { ...p, riesgo: { ...p.riesgo, [k]: Number(v) } } : p));

  const guardar = useMutation({
    mutationFn: async () => {
      if (!f) return;
      await apiClient.post('/rpc/config_set', {
        _estrategia: f.estrategia, _symbol: f.symbol, _timeframe: f.timeframe,
        _params: f.params, _riesgo: f.riesgo, _ambiente: f.ambiente,
      }, TR);
      if (apiKey || apiSecret) {
        await apiClient.post('/rpc/set_api_keys', { _api_key: apiKey || null, _api_secret: apiSecret || null }, TR);
      }
    },
    onSuccess: () => { toast.success('Configuración guardada'); setApiKey(''); setApiSecret(''); void qc.invalidateQueries({ queryKey: ['trading-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const kill = useMutation({
    mutationFn: async (activar: boolean) => {
      let motivo: string | null = null;
      if (activar) {
        const v = await dlg.pedir('Motivo del kill switch', [{ key: 'motivo', label: 'Motivo', minimo: 0 }]);
        motivo = v?.motivo || 'activado manualmente';
      }
      await apiClient.post('/rpc/kill_switch_set', { _activo: activar, _motivo: motivo }, TR);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['trading-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const toggleActivo = useMutation({
    mutationFn: async (activar: boolean) => (await apiClient.post('/rpc/activo_set', { _activo: activar }, TR)).data,
    onSuccess: (r) => { toast.success(r?.message || 'Actualizado'); void qc.invalidateQueries({ queryKey: ['trading-config'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !f) return <div className="flex items-center justify-center p-12"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;

  const hb = f.estado?.heartbeat ? new Date(f.estado.heartbeat) : null;
  const online = hb ? (Date.now() - hb.getTime()) < 5 * 60 * 1000 : false;

  return (
    <div className="max-w-3xl space-y-6 p-6">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <Settings className="h-8 w-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Binance — Configuración</h1>
          <p className="text-sm text-muted-foreground">Bot de trading (cuenta global). Empieza siempre en testnet.</p>
        </div>
      </div>

      {/* Estado del bot + kill switch */}
      <Card className={f.kill_switch ? 'border-red-500/40 bg-red-50/40 dark:bg-red-900/10' : 'border-border'}>
        <CardContent className="flex flex-wrap items-center justify-between gap-4 p-4">
          <div className="flex items-center gap-3 text-sm">
            <Activity className={`h-5 w-5 ${online ? 'text-green-600' : 'text-muted-foreground'}`} />
            <span>Motor: <b>{online ? 'en línea' : 'sin latido'}</b>{hb && <span className="text-muted-foreground"> · {hb.toLocaleString(LOCALE)}</span>}</span>
            <span className="text-muted-foreground">|</span>
            <span>Ambiente: <b className="uppercase">{f.ambiente}</b></span>
            <span className="text-muted-foreground">|</span>
            <span>Estado: <b>{f.activo ? 'activo' : 'detenido'}</b></span>
          </div>
          <div className="flex items-center gap-2">
            {f.activo
              ? <Button size="sm" variant="outline" className="text-amber-600" isLoading={toggleActivo.isPending} onClick={() => toggleActivo.mutate(false)}><Square className="h-4 w-4" /> Detener</Button>
              : <Button size="sm" isLoading={toggleActivo.isPending} disabled={f.kill_switch || !f.api_configurada}
                  title={f.kill_switch ? 'Desactiva el kill switch primero' : (!f.api_configurada ? 'Configura las claves API primero' : '')}
                  onClick={() => toggleActivo.mutate(true)}><Play className="h-4 w-4" /> Iniciar</Button>}
            <span className="text-muted-foreground">·</span>
            {f.kill_switch
              ? <><span className="flex items-center gap-1 text-sm text-red-600"><ShieldAlert className="h-4 w-4" /> Kill switch ACTIVO</span>
                  <Button size="sm" variant="outline" isLoading={kill.isPending} onClick={() => kill.mutate(false)}>Desactivar</Button></>
              : <><span className="flex items-center gap-1 text-sm text-green-600"><ShieldCheck className="h-4 w-4" /> Operación permitida</span>
                  <Button size="sm" variant="outline" className="text-red-600" isLoading={kill.isPending} onClick={() => kill.mutate(true)}>Kill switch</Button></>}
          </div>
        </CardContent>
      </Card>
      {f.kill_switch && f.kill_switch_motivo && <p className="-mt-4 text-xs text-red-600">Motivo: {f.kill_switch_motivo} · la reactivación es manual.</p>}

      {/* Estrategia y mercado */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Estrategia y mercado</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs font-medium">Ambiente</label>
            <select className={inputCls} value={f.ambiente} onChange={(e) => set('ambiente', e.target.value)}>
              <option value="testnet">Testnet (paper)</option>
              <option value="produccion">Producción (dinero real)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Estrategia</label>
            <select className={inputCls} value={f.estrategia} onChange={(e) => set('estrategia', e.target.value)}>
              <option value="sma_cross">Cruce de medias (SMA)</option>
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Par (symbol)</label>
            <input className={inputCls} placeholder="BTC/USDT" value={f.symbol} onChange={(e) => set('symbol', e.target.value.toUpperCase())} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">Timeframe</label>
            <select className={inputCls} value={f.timeframe} onChange={(e) => set('timeframe', e.target.value)}>
              {TIMEFRAMES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">SMA rápida (períodos)</label>
            <input type="number" min={1} className={inputCls} value={f.params.sma_rapida ?? ''} onChange={(e) => setParam('sma_rapida', e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">SMA lenta (períodos)</label>
            <input type="number" min={1} className={inputCls} value={f.params.sma_lenta ?? ''} onChange={(e) => setParam('sma_lenta', e.target.value)} />
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs font-medium">Filtro de tendencia — SMA larga (0 = sin filtro)</label>
            <input type="number" min={0} className={inputCls} value={f.params.sma_tendencia ?? 0} onChange={(e) => setParam('sma_tendencia', e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Solo abre largos si el precio está por encima de esta media (p. ej. 200). Evita comprar en mercado bajista.</p>
          </div>
          <div className="space-y-1 sm:col-span-2">
            <label className="text-xs font-medium">Anti-whipsaw — umbral de cruce (fracción; 0 = sin banda)</label>
            <input type="number" min={0} step="any" className={inputCls} value={f.params.umbral_cruce_pct ?? 0} onChange={(e) => setParam('umbral_cruce_pct', e.target.value)} />
            <p className="text-[11px] text-muted-foreground">Exige que la SMA rápida supere a la lenta por este margen (p. ej. 0.01 = 1%). Útil en timeframes bajos con muchos cruces.</p>
          </div>
        </CardContent>
      </Card>

      {/* Gestión de riesgo */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Gestión de riesgo</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          {RIESGO_CAMPOS.map(({ k, label }) => (
            <div key={k} className="space-y-1">
              <label className="text-xs font-medium">{label}</label>
              <input type="number" step="any" className={inputCls} value={(f.riesgo?.[k] as number | undefined) ?? ''} onChange={(e) => setRiesgo(k, e.target.value)} />
            </div>
          ))}
          <label className="flex items-center gap-2 text-xs font-medium sm:col-span-2">
            <input type="checkbox" checked={Boolean(f.riesgo?.trailing)}
              onChange={(e) => setF((p) => (p ? { ...p, riesgo: { ...p.riesgo, trailing: e.target.checked } } : p))} />
            Trailing stop (el stop sube con el precio; nunca lo baja)
          </label>
        </CardContent>
      </Card>

      {/* Claves API */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><KeyRound className="h-4 w-4" /> Claves API de Binance</CardTitle></CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1">
            <label className="text-xs font-medium">API Key {f.api_configurada && <span className="text-green-600">(configurada)</span>}</label>
            <input type="password" autoComplete="new-password" className={inputCls} placeholder={f.api_configurada ? 'En blanco = no cambiar' : 'API Key'} value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
          </div>
          <div className="space-y-1">
            <label className="text-xs font-medium">API Secret {f.api_configurada && <span className="text-green-600">(configurada)</span>}</label>
            <input type="password" autoComplete="new-password" className={inputCls} placeholder={f.api_configurada ? 'En blanco = no cambiar' : 'API Secret'} value={apiSecret} onChange={(e) => setApiSecret(e.target.value)} />
          </div>
          <p className="sm:col-span-2 text-[11px] text-muted-foreground">
            Usa claves con permiso de <b>lectura + spot</b> únicamente; <b>NUNCA</b> habilites retiros. En testnet, genera las claves en testnet.binance.vision. Se guardan cifradas y solo el motor las descifra.
          </p>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>
          {!guardar.isPending && <Save className="h-4 w-4" />} Guardar configuración
        </Button>
      </div>
    </div>
  );
}
